/**
 * WeatherModule — full weather page with hourly curves, 10-day forecast,
 * air quality, sun cycle, and watched-cities picker.
 *
 * UX:
 *   - Top: city picker (current device location + watched cities, "+ city").
 *   - Hero: icon, current temperature, feels-like, condition label.
 *   - Chart: 24h curves (T, rain, wind) — tabs select which series leads.
 *   - 10-day list: icon, max/min, precip%, wind, sunrise/sunset.
 *   - Details: humidity, wind speed/direction, pressure, UV index.
 *   - Air quality: AQI + key pollutants when the AQI endpoint succeeds.
 *
 * Data: WeatherService (Open-Meteo) — single combined fetch per location,
 * shared by the home widget. Watched cities live in WatchedCitiesService.
 */

import type { AweniContext, AweniModule } from '../../types';
import type { WeatherDetails, WatchedCity } from '../home/types';
import { LocationService } from '../../services/LocationService';
import { WeatherService, type GeocodeResult } from '../../services/WeatherService';
import { prayerTimeService } from '../../services/PrayerTimeService';
import {
  getWatchedCitiesService,
  type WatchedCitiesService,
} from '../../services/WatchedCitiesService';
import { renderWeatherChart, renderTideChart } from './WeatherChart';
import { getWmoEntry } from './wmoIcons';
import { SourceAttributionStrip } from '../../shell/SourceAttributionStrip';

type ChartMode = 'all' | 'temp' | 'rain' | 'wind';

interface ActiveLocation {
  id: string; // 'current' or watched city id
  lat: number;
  lng: number;
  name: string;
  isCurrent: boolean;
}

export default class WeatherModule implements AweniModule {
  private container: HTMLElement | null = null;
  private ctx: AweniContext | null = null;
  /** Phase 3b — SourceAttributionStrip mounted below the weather hero card.
   *  Recreated on each renderHero() so location changes are reflected. */
  private attrStrip: SourceAttributionStrip | null = null;
  private locationService = new LocationService();
  private weatherService = new WeatherService(this.locationService);
  private watched: WatchedCitiesService = getWatchedCitiesService();

  private active: ActiveLocation | null = null;
  private details: WeatherDetails | null = null;
  private chartMode: ChartMode = 'all';
  private searchOpen = false;
  private searchResults: GeocodeResult[] = [];
  private searching = false;
  private searchTimer: number | null = null;
  private unsubWatched: (() => void) | null = null;

  async mount(container: HTMLElement, context: AweniContext): Promise<void> {
    this.container = container;
    this.ctx = context;

    container.style.cssText = 'min-height:100%;background:var(--bg-primary);';

    // Pick initial active location: LocationService → PrayerTimeService
    // fallback → first watched city. Two services have independent storage
    // (aweni_location vs aweni_prayer_location) — if Prayer was the entry
    // point that captured the user's location, mirror it back here so the
    // Weather page renders the same city the Home hero shows.
    let loc = this.locationService.getState();
    if (loc.lat === null || loc.lng === null) {
      const prayerLoc = prayerTimeService.getLocation();
      if (prayerLoc && Number.isFinite(prayerLoc.lat) && Number.isFinite(prayerLoc.lng)) {
        // Sync back so future LocationService consumers see it too.
        this.locationService.setManualLocation(
          prayerLoc.lat,
          prayerLoc.lng,
          prayerLoc.city || 'Current location',
        );
        loc = this.locationService.getState();
      }
    }

    if (loc.lat !== null && loc.lng !== null) {
      this.active = {
        id: 'current',
        lat: loc.lat,
        lng: loc.lng,
        name: loc.city || 'Current location',
        isCurrent: true,
      };
    } else {
      const first = this.watched.list()[0];
      if (first) {
        this.active = {
          id: first.id,
          lat: first.lat,
          lng: first.lng,
          name: first.name,
          isCurrent: false,
        };
      }
    }

    // Hydrate from cache + render shell, then revalidate in background.
    if (this.active) {
      this.details = this.weatherService.peekDetails(this.active.lat, this.active.lng);
    }
    this.render();

    if (this.active) {
      void this.refreshActive();
    }

    this.unsubWatched = this.watched.onChange(() => this.render());
  }

  unmount(): void {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    this.unsubWatched?.();
    this.unsubWatched = null;
    this.attrStrip?.unmount();
    this.attrStrip = null;
    this.container = null;
  }

  // ---------- data ----------

  private async refreshActive(): Promise<void> {
    if (!this.active) return;
    const a = this.active;
    const fresh = await this.weatherService.fetchDetails(a.lat, a.lng, a.name);
    if (fresh && this.active?.id === a.id) {
      this.details = fresh;
      this.render();
    }
  }

  private setActive(loc: ActiveLocation): void {
    this.active = loc;
    this.details = this.weatherService.peekDetails(loc.lat, loc.lng);
    this.render();
    void this.refreshActive();
  }

  // ---------- render ----------

  private render(): void {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    if (!this.active) {
      this.container.innerHTML = `
        <div style="padding:32px 20px;text-align:center;">
          <div style="font-size:44px;margin-bottom:12px;">\u{1F324}️</div>
          <h2 style="font-size:18px;font-weight:700;color:var(--text-primary);margin-bottom:8px;">
            ${escHtml(t('weather_title'))}
          </h2>
          <p style="font-size:14px;color:var(--text-secondary);margin-bottom:20px;">
            ${escHtml(t('weather_no_location'))}
          </p>
          <button data-act="search-toggle"
            style="padding:10px 20px;border-radius:12px;background:var(--accent);color:var(--accent-text);
                   font-weight:600;border:none;cursor:pointer;">
            ${escHtml(t('weather_add_city'))}
          </button>
          <div data-region="search" style="margin-top:16px;text-align:start;"></div>
        </div>
      `;
      this.attachHandlers();
      this.renderSearch();
      return;
    }

    this.container.innerHTML = `
      <div style="max-width:560px;margin:0 auto;padding:12px 16px 32px;">
        <div data-region="picker"></div>
        <div data-region="hero"></div>
        <div data-region="chart-card"></div>
        <div data-region="daily"></div>
        <div data-region="tides"></div>
        <div data-region="astro"></div>
        <div data-region="details"></div>
        <div data-region="air"></div>
        <div data-region="pollen"></div>
        <div data-region="search" style="margin-top:14px;"></div>
      </div>
    `;
    this.renderPicker();
    this.renderHero();
    this.renderChart();
    this.renderDaily();
    this.renderTides();
    this.renderAstro();
    this.renderDetails();
    this.renderAir();
    this.renderPollen();
    this.renderSearch();
    this.attachHandlers();
  }

  private renderPicker(): void {
    const host = this.container?.querySelector('[data-region="picker"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.active) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    const loc = this.locationService.getState();
    const items: ActiveLocation[] = [];
    if (loc.lat !== null && loc.lng !== null) {
      items.push({
        id: 'current',
        lat: loc.lat,
        lng: loc.lng,
        name: loc.city || t('weather_current_location'),
        isCurrent: true,
      });
    }
    for (const c of this.watched.list()) {
      items.push({ id: c.id, lat: c.lat, lng: c.lng, name: c.name, isCurrent: false });
    }

    const chips = items
      .map((item) => {
        const isActive = item.id === this.active!.id;
        const removeBtn =
          !item.isCurrent && isActive
            ? `<button data-act="remove-city" data-id="${escAttr(item.id)}"
                aria-label="${escAttr(t('weather_remove_city'))}"
                style="margin-inline-start:4px;background:none;border:none;color:inherit;
                       cursor:pointer;font-size:14px;line-height:1;padding:0 2px;">×</button>`
            : '';
        const pin = item.isCurrent ? '\u{1F4CD} ' : '';
        return `
          <button data-act="pick-city" data-id="${escAttr(item.id)}"
            style="display:inline-flex;align-items:center;gap:4px;padding:6px 12px;
                   border-radius:999px;border:1px solid ${isActive ? 'var(--accent)' : 'var(--border)'};
                   background:${isActive ? 'var(--accent)' : 'var(--card-bg)'};
                   color:${isActive ? 'var(--accent-text)' : 'var(--text-primary)'};
                   font-size:13px;font-weight:${isActive ? 600 : 500};
                   cursor:pointer;white-space:nowrap;">
            ${pin}${escHtml(item.name)}${removeBtn}
          </button>`;
      })
      .join('');

    host.innerHTML = `
      <div style="display:flex;gap:8px;overflow-x:auto;padding-bottom:6px;
                  scrollbar-width:none;-ms-overflow-style:none;">
        ${chips}
        <button data-act="search-toggle"
          style="display:inline-flex;align-items:center;gap:4px;padding:6px 12px;
                 border-radius:999px;border:1px dashed var(--border);background:transparent;
                 color:var(--text-secondary);font-size:13px;cursor:pointer;white-space:nowrap;">
          + ${escHtml(this.ctx!.i18n.t('weather_add_city'))}
        </button>
      </div>
    `;
  }

  private renderHero(): void {
    const host = this.container?.querySelector('[data-region="hero"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.active) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const d = this.details;
    if (!d) {
      host.innerHTML = `
        <div style="background:var(--card-bg);border-radius:20px;padding:32px 16px;text-align:center;
                    box-shadow:var(--shadow-sm);margin-top:12px;">
          <div class="aweni-spinner" style="margin:0 auto 12px;"></div>
          <p style="font-size:13px;color:var(--text-secondary);">
            ${escHtml(t('weather_loading'))}
          </p>
        </div>`;
      return;
    }
    const wmo = getWmoEntry(d.current.weatherCode);
    const icon = d.current.isDay ? wmo.iconDay : wmo.iconNight;
    const label = t(wmo.labelKey);
    const updated = formatRelative(d.fetchedAt, this.ctx.i18n.locale, t);

    host.innerHTML = `
      <div style="background:var(--card-bg);border-radius:20px;padding:18px 18px 14px;
                  box-shadow:var(--shadow-sm);margin-top:12px;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
          <div style="display:flex;align-items:center;gap:14px;">
            <div style="font-size:64px;line-height:1;">${icon}</div>
            <div>
              <div style="font-size:42px;font-weight:800;color:var(--text-primary);line-height:1;">
                ${Math.round(d.current.temperature)}°
              </div>
              <div style="font-size:13px;color:var(--text-secondary);margin-top:4px;">
                ${escHtml(label)}
              </div>
              <div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">
                ${escHtml(t('weather_feels_like'))} ${Math.round(d.current.feelsLike)}°
              </div>
            </div>
          </div>
          <div style="text-align:end;">
            <div style="font-size:13px;color:var(--text-primary);font-weight:600;">
              ↑ ${Math.round(d.daily[0]?.tempMax ?? d.current.temperature)}°
              · ↓ ${Math.round(d.daily[0]?.tempMin ?? d.current.temperature)}°
            </div>
            <div style="font-size:11px;color:var(--text-secondary);margin-top:4px;">
              ${escHtml(updated)}
            </div>
          </div>
        </div>
        <!-- Phase 3b (2026-05-16) — SourceAttributionStrip: shows data sources
             behind this weather card (Hijri authority auto-added + Open-Meteo +
             OpenStreetMap Nominatim). In-app info popovers, NO external links. -->
        <div data-source-attribution style="margin-top:14px;display:block;"></div>
      </div>
    `;

    // Mount the attribution strip into the placeholder.
    this.attrStrip?.unmount();
    const attrSlot = host.querySelector<HTMLElement>('[data-source-attribution]');
    if (attrSlot) {
      this.attrStrip = new SourceAttributionStrip({
        id: 'weather-hero',
        rows: [
          {
            id: 'weather-provider',
            label: t('attribution_weather_provider_label') || 'Weather provider',
            value: t('attribution_weather_provider_value') || 'Open-Meteo (free, no API key)',
            info: t('attribution_weather_provider_info') || 'Hourly forecasts, current conditions, and humidity come from Open-Meteo — a free, open weather API. Data is cached locally for 30 minutes.',
          },
          {
            id: 'geocoding',
            label: t('attribution_geocoding_label') || 'Geocoding',
            value: t('attribution_geocoding_value') || 'OpenStreetMap Nominatim',
            info: t('attribution_geocoding_info') || 'City names → coordinates use the OpenStreetMap Nominatim service. We cache results locally so the same city is never looked up twice.',
          },
        ],
        strings: {
          setCity: t('calendar_hijri_ref_prompt') || 'Set your city',
          setCityTooltip: t('calendar_hijri_ref_prompt_tooltip') || 'Weather depends on your location. Set your city to load forecasts.',
          expandAria: t('attribution_expand') || 'Show sources used on this page',
          collapseAria: t('attribution_collapse') || 'Hide sources',
          infoAria: t('attribution_info') || 'More about this source',
          hijriAuthorityLabel: t('attribution_hijri_authority') || 'Hijri authority',
        },
      });
      this.attrStrip.mount(attrSlot);
    }
  }

  private renderChart(): void {
    const host = this.container?.querySelector('[data-region="chart-card"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.details) {
      if (host) host.innerHTML = '';
      return;
    }
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    const tabs: Array<{ id: ChartMode; label: string }> = [
      { id: 'all', label: t('weather_chart_all') },
      { id: 'temp', label: t('weather_chart_temp') },
      { id: 'rain', label: t('weather_chart_rain') },
      { id: 'wind', label: t('weather_chart_wind') },
    ];
    const tabHtml = tabs
      .map(
        (tab) => `
        <button data-act="chart-mode" data-mode="${tab.id}"
          style="padding:5px 11px;border-radius:999px;font-size:12px;font-weight:500;
                 border:1px solid ${this.chartMode === tab.id ? 'var(--accent)' : 'var(--border)'};
                 background:${this.chartMode === tab.id ? 'var(--accent)' : 'transparent'};
                 color:${this.chartMode === tab.id ? 'var(--accent-text)' : 'var(--text-secondary)'};
                 cursor:pointer;">${escHtml(tab.label)}</button>
      `,
      )
      .join('');

    host.innerHTML = `
      <div style="background:var(--card-bg);border-radius:20px;padding:14px 14px 6px;
                  box-shadow:var(--shadow-sm);margin-top:12px;">
        <div style="display:flex;align-items:center;justify-content:space-between;
                    margin-bottom:10px;flex-wrap:wrap;gap:8px;">
          <h3 style="font-size:14px;font-weight:700;color:var(--text-primary);margin:0;">
            ${escHtml(t('weather_chart_title'))}
          </h3>
          <div style="display:flex;gap:6px;flex-wrap:wrap;">${tabHtml}</div>
        </div>
        <div data-region="chart-svg"></div>
        <div style="display:flex;gap:14px;flex-wrap:wrap;margin-top:6px;
                    font-size:11px;color:var(--text-secondary);">
          <span><span style="display:inline-block;width:10px;height:2px;background:var(--accent);
                              vertical-align:middle;margin-inline-end:4px;"></span>${escHtml(t('weather_temp'))}</span>
          <span><span style="display:inline-block;width:10px;height:8px;background:#3B82F6;opacity:0.55;
                              vertical-align:middle;margin-inline-end:4px;border-radius:2px;"></span>${escHtml(t('weather_rain'))}</span>
          <span><span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:#94A3B8;
                              vertical-align:middle;margin-inline-end:4px;"></span>${escHtml(t('weather_wind'))}</span>
        </div>
      </div>
    `;
    const svgHost = host.querySelector('[data-region="chart-svg"]') as HTMLElement;
    renderWeatherChart(svgHost, this.details, {
      height: 180,
      hourLabels: true,
      show: {
        temp: this.chartMode === 'all' || this.chartMode === 'temp',
        rain: this.chartMode === 'all' || this.chartMode === 'rain',
        wind: this.chartMode === 'all' || this.chartMode === 'wind',
      },
      ariaLabel: t('weather_chart_title'),
    });
  }

  private renderDaily(): void {
    const host = this.container?.querySelector('[data-region="daily"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.details) {
      if (host) host.innerHTML = '';
      return;
    }
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const locale = this.ctx.i18n.locale;
    const days = this.details.daily.slice(0, 10);
    const allMax = Math.max(...days.map((d) => d.tempMax));
    const allMin = Math.min(...days.map((d) => d.tempMin));
    const span = Math.max(1, allMax - allMin);

    const rows = days
      .map((d, i) => {
        const date = new Date(d.date);
        const dayLabel = i === 0
          ? t('weather_today')
          : date.toLocaleDateString(locale, { weekday: 'short' });
        const dateLabel = date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
        const wmo = getWmoEntry(d.weatherCode);
        const leftPct = ((d.tempMin - allMin) / span) * 100;
        const widthPct = ((d.tempMax - d.tempMin) / span) * 100;
        const probLabel = d.precipitationProbability > 5
          ? `<span style="font-size:11px;color:#3B82F6;margin-inline-start:4px;">\u{1F4A7} ${d.precipitationProbability}%</span>`
          : '';
        return `
          <li style="display:grid;grid-template-columns:64px 28px 1fr auto;align-items:center;
                     gap:8px;padding:8px 4px;border-top:${i === 0 ? '0' : '1px solid var(--border)'};">
            <div>
              <div style="font-size:13px;font-weight:600;color:var(--text-primary);">
                ${escHtml(dayLabel)}
              </div>
              <div style="font-size:11px;color:var(--text-secondary);">${escHtml(dateLabel)}</div>
            </div>
            <div style="font-size:22px;text-align:center;" title="${escAttr(t(wmo.labelKey))}">
              ${wmo.iconDay}
            </div>
            <div style="position:relative;height:6px;border-radius:999px;background:var(--bg-secondary);">
              <div style="position:absolute;top:0;bottom:0;left:${leftPct.toFixed(1)}%;
                          width:${Math.max(8, widthPct).toFixed(1)}%;
                          background:linear-gradient(90deg,#60A5FA,#F59E0B,#EF4444);
                          border-radius:999px;"></div>
            </div>
            <div style="font-size:12px;font-weight:600;color:var(--text-primary);min-width:78px;text-align:end;">
              ${Math.round(d.tempMin)}° · ${Math.round(d.tempMax)}°${probLabel}
            </div>
          </li>`;
      })
      .join('');

    host.innerHTML = `
      <div style="background:var(--card-bg);border-radius:20px;padding:14px 14px 8px;
                  box-shadow:var(--shadow-sm);margin-top:12px;">
        <h3 style="font-size:14px;font-weight:700;color:var(--text-primary);margin:0 0 4px;">
          ${escHtml(t('weather_10_day'))}
        </h3>
        <ul style="list-style:none;padding:0;margin:0;">${rows}</ul>
      </div>
    `;
  }

  private renderDetails(): void {
    const host = this.container?.querySelector('[data-region="details"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.details) {
      if (host) host.innerHTML = '';
      return;
    }
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const locale = this.ctx.i18n.locale;
    const c = this.details.current;
    const today = this.details.daily[0];
    const wd = formatWindDirection(c.windDirection);
    const sr = today?.sunrise ? formatLocalTime(today.sunrise, locale) : '—';
    const ss = today?.sunset ? formatLocalTime(today.sunset, locale) : '—';
    const uvVal = Math.round(c.uvIndex || today?.uvMax || 0);
    const uvBand = uvBandLabel(uvVal, t);
    const visKm = c.visibility != null ? (c.visibility / 1000).toFixed(c.visibility >= 10000 ? 0 : 1) : null;
    const dewStr = c.dewPoint != null ? `${Math.round(c.dewPoint)}°` : null;
    const cloudParts = [c.cloudCoverLow, c.cloudCoverMid, c.cloudCoverHigh].some(
      (x) => typeof x === 'number',
    );

    const cell = (label: string, value: string, sub = '') => `
      <div style="background:var(--bg-secondary);border-radius:14px;padding:10px 12px;">
        <div style="font-size:11px;color:var(--text-secondary);">${escHtml(label)}</div>
        <div style="font-size:15px;font-weight:700;color:var(--text-primary);margin-top:2px;">
          ${escHtml(value)}
        </div>
        ${sub ? `<div style="font-size:11px;color:var(--text-secondary);margin-top:2px;">${escHtml(sub)}</div>` : ''}
      </div>`;

    const cloudCell = cloudParts
      ? `
      <div style="grid-column:1/-1;background:var(--bg-secondary);border-radius:14px;padding:10px 12px;">
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:6px;">
          ${escHtml(t('weather_cloud_layers'))} · ${Math.round(c.cloudCover)}%
        </div>
        ${cloudBars(c.cloudCoverLow, c.cloudCoverMid, c.cloudCoverHigh, t)}
      </div>`
      : '';

    host.innerHTML = `
      <div style="background:var(--card-bg);border-radius:20px;padding:14px;
                  box-shadow:var(--shadow-sm);margin-top:12px;">
        <h3 style="font-size:14px;font-weight:700;color:var(--text-primary);margin:0 0 10px;">
          ${escHtml(t('weather_details'))}
        </h3>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;">
          ${cell(t('weather_humidity'), `${Math.round(c.humidity)}%`)}
          ${cell(t('weather_wind'), `${Math.round(c.windSpeed)} km/h`, wd)}
          ${cell(t('weather_pressure'), `${Math.round(c.pressure)} hPa`)}
          ${cell(t('weather_uv_index'), String(uvVal), uvBand)}
          ${cell(t('weather_sunrise'), sr)}
          ${cell(t('weather_sunset'), ss)}
          ${visKm != null ? cell(t('weather_visibility'), `${visKm} km`) : ''}
          ${dewStr != null ? cell(t('weather_dew_point'), dewStr) : ''}
          ${cloudCell}
        </div>
      </div>
    `;
  }

  private renderTides(): void {
    const host = this.container?.querySelector('[data-region="tides"]') as HTMLElement | null;
    if (!host || !this.ctx) return;
    const m = this.details?.marine;
    if (!m || m.seaLevel.length < 4) {
      host.innerHTML = '';
      return;
    }
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const locale = this.ctx.i18n.locale;
    const upcoming = m.tides.slice(0, 4);
    const items = upcoming
      .map((ev) => {
        const time = formatLocalTime(ev.time, locale);
        const isHigh = ev.type === 'high';
        const arrow = isHigh ? '▲' : '▼';
        const color = isHigh ? '#0EA5E9' : '#94A3B8';
        const label = isHigh ? t('weather_tide_high') : t('weather_tide_low');
        return `
          <div style="display:flex;align-items:center;gap:8px;background:var(--bg-secondary);
                      border-radius:12px;padding:8px 10px;">
            <span style="color:${color};font-size:14px;font-weight:700;">${arrow}</span>
            <div>
              <div style="font-size:12px;font-weight:600;color:var(--text-primary);">
                ${escHtml(label)}
              </div>
              <div style="font-size:11px;color:var(--text-secondary);">
                ${escHtml(time)} · ${ev.height.toFixed(2)}m
              </div>
            </div>
          </div>`;
      })
      .join('');

    const sst = m.seaSurfaceTemp != null
      ? `<span>\u{1F30A} ${Math.round(m.seaSurfaceTemp)}° ${escHtml(t('weather_sea_temp'))}</span>`
      : '';
    const wave = m.waveHeight != null
      ? `<span>\u{1F30A} ${m.waveHeight.toFixed(1)}m ${escHtml(t('weather_wave_height'))}${m.wavePeriod != null ? ` · ${Math.round(m.wavePeriod)}s` : ''}</span>`
      : '';

    host.innerHTML = `
      <div style="background:var(--card-bg);border-radius:20px;padding:14px;
                  box-shadow:var(--shadow-sm);margin-top:12px;">
        <h3 style="font-size:14px;font-weight:700;color:var(--text-primary);margin:0 0 8px;">
          ${escHtml(t('weather_tides'))}
        </h3>
        <div data-region="tides-svg" style="margin-bottom:8px;"></div>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));
                    gap:8px;margin-bottom:8px;">
          ${items}
        </div>
        ${sst || wave
          ? `<div style="display:flex;flex-wrap:wrap;gap:14px;font-size:12px;
                         color:var(--text-secondary);">${sst}${wave}</div>`
          : ''}
      </div>
    `;
    const svgHost = host.querySelector('[data-region="tides-svg"]') as HTMLElement;
    renderTideChart(svgHost, m.seaLevel, m.tides, { height: 130 });
  }

  private renderAstro(): void {
    const host = this.container?.querySelector('[data-region="astro"]') as HTMLElement | null;
    if (!host || !this.ctx) return;
    const moon = this.details?.moon;
    if (!moon) {
      host.innerHTML = '';
      return;
    }
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const locale = this.ctx.i18n.locale;
    const today = this.details!.daily[0];
    const sr = today?.sunrise ? formatLocalTime(today.sunrise, locale) : null;
    const ss = today?.sunset ? formatLocalTime(today.sunset, locale) : null;
    const dayLengthLabel =
      today?.sunrise && today?.sunset
        ? formatDayLength(today.sunrise, today.sunset)
        : null;

    host.innerHTML = `
      <div style="background:var(--card-bg);border-radius:20px;padding:14px;
                  box-shadow:var(--shadow-sm);margin-top:12px;">
        <h3 style="font-size:14px;font-weight:700;color:var(--text-primary);margin:0 0 10px;">
          ${escHtml(t('weather_sun_moon'))}
        </h3>
        <div style="display:flex;align-items:center;gap:14px;">
          <div style="font-size:46px;line-height:1;" aria-hidden="true">${moon.emoji}</div>
          <div style="flex:1;">
            <div style="font-size:13px;font-weight:600;color:var(--text-primary);">
              ${escHtml(t(moon.nameKey))}
            </div>
            <div style="font-size:11px;color:var(--text-secondary);margin-top:2px;">
              ${escHtml(t('weather_moon_illumination'))}: ${moon.illumination}%
            </div>
            <div style="margin-top:6px;height:5px;border-radius:999px;background:var(--bg-secondary);
                        overflow:hidden;">
              <div style="width:${moon.illumination}%;height:100%;background:#94A3B8;"></div>
            </div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-top:10px;">
          ${sr ? `
            <div style="background:var(--bg-secondary);border-radius:12px;padding:8px;text-align:center;">
              <div style="font-size:11px;color:var(--text-secondary);">${escHtml(t('weather_sunrise'))}</div>
              <div style="font-size:13px;font-weight:700;color:var(--text-primary);margin-top:2px;">${escHtml(sr)}</div>
            </div>` : ''}
          ${ss ? `
            <div style="background:var(--bg-secondary);border-radius:12px;padding:8px;text-align:center;">
              <div style="font-size:11px;color:var(--text-secondary);">${escHtml(t('weather_sunset'))}</div>
              <div style="font-size:13px;font-weight:700;color:var(--text-primary);margin-top:2px;">${escHtml(ss)}</div>
            </div>` : ''}
          ${dayLengthLabel ? `
            <div style="background:var(--bg-secondary);border-radius:12px;padding:8px;text-align:center;">
              <div style="font-size:11px;color:var(--text-secondary);">${escHtml(t('weather_day_length'))}</div>
              <div style="font-size:13px;font-weight:700;color:var(--text-primary);margin-top:2px;">${escHtml(dayLengthLabel)}</div>
            </div>` : ''}
        </div>
      </div>
    `;
  }

  private renderPollen(): void {
    const host = this.container?.querySelector('[data-region="pollen"]') as HTMLElement | null;
    if (!host || !this.ctx) return;
    const p = this.details?.pollen;
    if (!p || Object.keys(p).length === 0) {
      host.innerHTML = '';
      return;
    }
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const order: Array<keyof NonNullable<WeatherDetails['pollen']>> = [
      'alder',
      'birch',
      'grass',
      'mugwort',
      'olive',
      'ragweed',
    ];
    const rows = order
      .map((k) => {
        const v = p[k];
        if (v == null) return '';
        // Bands roughly aligned with European pollen-info thresholds in
        // grains/m³. Different per species in reality but the UI just
        // needs a coarse signal.
        const band = pollenBand(v, t);
        const bar = Math.min(100, Math.round((v / 50) * 100));
        return `
          <div style="display:grid;grid-template-columns:80px 1fr 90px;align-items:center;
                      gap:8px;padding:6px 0;">
            <div style="font-size:12px;font-weight:600;color:var(--text-primary);">
              ${escHtml(t(`weather_pollen_${k}`))}
            </div>
            <div style="height:6px;border-radius:999px;background:var(--bg-secondary);overflow:hidden;">
              <div style="width:${bar}%;height:100%;background:${band.color};"></div>
            </div>
            <div style="font-size:11px;color:${band.color};font-weight:600;text-align:end;">
              ${escHtml(band.label)} (${v.toFixed(0)})
            </div>
          </div>`;
      })
      .join('');

    host.innerHTML = `
      <div style="background:var(--card-bg);border-radius:20px;padding:14px;
                  box-shadow:var(--shadow-sm);margin-top:12px;">
        <h3 style="font-size:14px;font-weight:700;color:var(--text-primary);margin:0 0 4px;">
          ${escHtml(t('weather_pollen'))}
        </h3>
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:6px;">
          ${escHtml(t('weather_pollen_unit'))}
        </div>
        ${rows}
      </div>
    `;
  }

  private renderAir(): void {
    const host = this.container?.querySelector('[data-region="air"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.details) {
      if (host) host.innerHTML = '';
      return;
    }
    const aq = this.details.airQuality;
    if (!aq || (aq.aqiEuropean == null && aq.aqiUs == null && aq.pm25 == null)) {
      host.innerHTML = '';
      return;
    }
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const aqi = aq.aqiEuropean ?? aq.aqiUs ?? 0;
    const band = europeanAqiBand(aqi, t);

    const stat = (label: string, value: string) => `
      <div style="text-align:center;background:var(--bg-secondary);border-radius:12px;padding:8px;">
        <div style="font-size:11px;color:var(--text-secondary);">${escHtml(label)}</div>
        <div style="font-size:13px;font-weight:700;color:var(--text-primary);margin-top:2px;">
          ${escHtml(value)}
        </div>
      </div>`;

    host.innerHTML = `
      <div style="background:var(--card-bg);border-radius:20px;padding:14px;
                  box-shadow:var(--shadow-sm);margin-top:12px;">
        <h3 style="font-size:14px;font-weight:700;color:var(--text-primary);margin:0 0 10px;">
          ${escHtml(t('weather_air_quality'))}
        </h3>
        <div style="display:flex;align-items:center;gap:14px;margin-bottom:10px;">
          <div style="font-size:30px;font-weight:800;color:${band.color};">
            ${Math.round(aqi)}
          </div>
          <div>
            <div style="font-size:13px;font-weight:600;color:var(--text-primary);">
              ${escHtml(band.label)}
            </div>
            <div style="font-size:11px;color:var(--text-secondary);">
              ${escHtml(t('weather_aqi_european'))}
            </div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:6px;">
          ${aq.pm25 != null ? stat('PM2.5', `${aq.pm25.toFixed(1)} µg/m³`) : ''}
          ${aq.pm10 != null ? stat('PM10', `${aq.pm10.toFixed(1)} µg/m³`) : ''}
          ${aq.o3 != null ? stat('O₃', `${aq.o3.toFixed(0)} µg/m³`) : ''}
          ${aq.no2 != null ? stat('NO₂', `${aq.no2.toFixed(0)} µg/m³`) : ''}
        </div>
      </div>
    `;
  }

  private renderSearch(): void {
    const host = this.container?.querySelector('[data-region="search"]') as HTMLElement | null;
    if (!host || !this.ctx) return;
    if (!this.searchOpen) {
      host.innerHTML = '';
      return;
    }
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    // Render the panel + input ONCE. Subsequent calls only update the
    // results region so typing never destroys the input element (focus +
    // caret position preserved). Bug fix 2026-05-06.
    const existingInput = host.querySelector('[data-act="search-input"]');
    if (!existingInput) {
      host.innerHTML = `
        <div style="background:var(--card-bg);border-radius:20px;padding:14px;box-shadow:var(--shadow-sm);">
          <label style="display:block;font-size:12px;color:var(--text-secondary);margin-bottom:6px;">
            ${escHtml(t('weather_search_label'))}
          </label>
          <input type="text" data-act="search-input" autocomplete="off"
            placeholder="${escAttr(t('weather_search_placeholder'))}"
            style="width:100%;padding:10px 12px;border-radius:12px;border:1px solid var(--border);
                   background:var(--bg-primary);color:var(--text-primary);font-size:14px;
                   box-sizing:border-box;"/>
          <div data-region="search-results" style="margin-top:8px;min-height:24px;"></div>
        </div>
      `;
      const input = host.querySelector('[data-act="search-input"]') as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.addEventListener('input', () => {
          const q = input.value;
          if (this.searchTimer) clearTimeout(this.searchTimer);
          if (q.trim().length < 2) {
            this.searchResults = [];
            this.searching = false;
            this.renderSearchResults();
            return;
          }
          this.searching = true;
          this.renderSearchResults();
          const ref = input;
          this.searchTimer = window.setTimeout(async () => {
            const lang = this.ctx?.i18n.locale.slice(0, 2) || 'en';
            const results = await this.weatherService.geocodeCity(ref.value, lang);
            if (ref.value === q) {
              this.searchResults = results;
              this.searching = false;
              this.renderSearchResults();
            }
          }, 250);
        });
      }
    }

    this.renderSearchResults();
  }

  /** Updates only the results region — leaves the input intact so typing
   *  never loses focus / caret position. */
  private renderSearchResults(): void {
    const host = this.container?.querySelector('[data-region="search"]') as HTMLElement | null;
    const results = host?.querySelector('[data-region="search-results"]') as HTMLElement | null;
    if (!results || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    const items = this.searchResults
      .map((r) => {
        const detail = [r.admin1, r.country].filter(Boolean).join(', ');
        return `
          <button data-act="add-result" data-lat="${r.latitude}" data-lng="${r.longitude}"
            data-name="${escAttr(r.name)}" data-cc="${escAttr(r.country_code || '')}"
            data-admin1="${escAttr(r.admin1 || '')}"
            style="display:block;width:100%;text-align:start;background:var(--bg-secondary);
                   border:none;border-radius:12px;padding:10px 12px;margin-top:6px;cursor:pointer;">
            <div style="font-size:14px;font-weight:600;color:var(--text-primary);">
              ${escHtml(r.name)}
            </div>
            <div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">
              ${escHtml(detail)}
            </div>
          </button>`;
      })
      .join('');

    results.innerHTML = `
      ${this.searching ? `<div style="font-size:12px;color:var(--text-secondary);">${escHtml(t('weather_searching'))}</div>` : ''}
      ${items}
      ${!this.searching && this.searchResults.length === 0
        ? `<div style="font-size:12px;color:var(--text-secondary);padding:6px 2px;">${escHtml(t('weather_search_hint'))}</div>`
        : ''}
    `;

    // Re-attach handlers on result buttons (they're freshly inserted)
    results.querySelectorAll<HTMLElement>('[data-act="add-result"]').forEach((el) => {
      el.addEventListener('click', (ev) => this.onAction('add-result', el, ev));
    });
  }

  private attachHandlers(): void {
    if (!this.container) return;
    this.container.querySelectorAll('[data-act]').forEach((el) => {
      const act = el.getAttribute('data-act');
      if (!act) return;
      el.addEventListener('click', (ev) => this.onAction(act, ev.currentTarget as HTMLElement, ev));
    });
  }

  private onAction(act: string, el: HTMLElement, ev: Event): void {
    if (act === 'pick-city') {
      ev.preventDefault();
      const id = el.getAttribute('data-id') || '';
      this.pickById(id);
    } else if (act === 'remove-city') {
      ev.preventDefault();
      ev.stopPropagation();
      const id = el.getAttribute('data-id') || '';
      this.watched.remove(id);
      // If the removed city was active, fall back to current location.
      if (this.active?.id === id) {
        const loc = this.locationService.getState();
        if (loc.lat !== null && loc.lng !== null) {
          this.setActive({
            id: 'current',
            lat: loc.lat,
            lng: loc.lng,
            name: loc.city || 'Current location',
            isCurrent: true,
          });
        } else {
          this.active = null;
          this.details = null;
          this.render();
        }
      } else {
        this.render();
      }
    } else if (act === 'search-toggle') {
      this.searchOpen = !this.searchOpen;
      this.searchResults = [];
      this.render();
    } else if (act === 'chart-mode') {
      const mode = el.getAttribute('data-mode') as ChartMode | null;
      if (mode) {
        this.chartMode = mode;
        this.renderChart();
        // Re-attach handlers in re-rendered region
        this.container
          ?.querySelectorAll('[data-region="chart-card"] [data-act]')
          .forEach((sub) => {
            const a = sub.getAttribute('data-act');
            if (a)
              sub.addEventListener('click', (e) =>
                this.onAction(a, e.currentTarget as HTMLElement, e),
              );
          });
      }
    } else if (act === 'add-result') {
      ev.preventDefault();
      const lat = parseFloat(el.getAttribute('data-lat') || '');
      const lng = parseFloat(el.getAttribute('data-lng') || '');
      const name = el.getAttribute('data-name') || '';
      const cc = el.getAttribute('data-cc') || undefined;
      const admin1 = el.getAttribute('data-admin1') || undefined;
      if (Number.isFinite(lat) && Number.isFinite(lng) && name) {
        const added = this.watched.add({ name, lat, lng, countryCode: cc, admin1 });
        if (added) {
          this.searchOpen = false;
          this.searchResults = [];
          this.setActive({
            id: added.id,
            lat: added.lat,
            lng: added.lng,
            name: added.name,
            isCurrent: false,
          });
        }
      }
    }
  }

  private pickById(id: string): void {
    if (id === 'current') {
      const loc = this.locationService.getState();
      if (loc.lat !== null && loc.lng !== null) {
        this.setActive({
          id: 'current',
          lat: loc.lat,
          lng: loc.lng,
          name: loc.city || 'Current location',
          isCurrent: true,
        });
      }
      return;
    }
    const c: WatchedCity | undefined = this.watched.list().find((x) => x.id === id);
    if (c) {
      this.setActive({ id: c.id, lat: c.lat, lng: c.lng, name: c.name, isCurrent: false });
    }
  }
}

// ---------------------------------------------------------------------------
// formatting helpers (pure)
// ---------------------------------------------------------------------------

function formatWindDirection(deg: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const i = Math.round(((deg % 360) / 45)) % 8;
  return `${dirs[i]} (${Math.round(deg)}°)`;
}

function formatLocalTime(isoLike: string, locale: string): string {
  const d = new Date(isoLike);
  if (isNaN(d.getTime())) return isoLike;
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function formatRelative(
  ts: number,
  _locale: string,
  t: (k: string, p?: Record<string, string | number>) => string,
): string {
  const diffSec = Math.max(0, (Date.now() - ts) / 1000);
  if (diffSec < 60) return t('weather_just_now');
  if (diffSec < 3600) return t('weather_minutes_ago', { n: Math.round(diffSec / 60) });
  if (diffSec < 86400) return t('weather_hours_ago', { n: Math.round(diffSec / 3600) });
  return t('weather_days_ago', { n: Math.round(diffSec / 86400) });
}

function uvBandLabel(uv: number, t: (k: string) => string): string {
  if (uv >= 11) return t('weather_uv_extreme');
  if (uv >= 8) return t('weather_uv_very_high');
  if (uv >= 6) return t('weather_uv_high');
  if (uv >= 3) return t('weather_uv_moderate');
  return t('weather_uv_low');
}

function pollenBand(v: number, t: (k: string) => string): { label: string; color: string } {
  // Coarse universal bands. Real thresholds vary per species (birch is far
  // more potent than mugwort at the same count) but a single scale keeps
  // the visual readable. Numbers in grains/m³.
  if (v < 1) return { label: t('weather_pollen_none'), color: '#10B981' };
  if (v < 10) return { label: t('weather_pollen_low'), color: '#84CC16' };
  if (v < 30) return { label: t('weather_pollen_moderate'), color: '#F59E0B' };
  if (v < 50) return { label: t('weather_pollen_high'), color: '#DC2626' };
  return { label: t('weather_pollen_very_high'), color: '#9333EA' };
}

function cloudBars(
  low: number | undefined,
  mid: number | undefined,
  high: number | undefined,
  t: (k: string) => string,
): string {
  const layer = (label: string, v: number | undefined, color: string) => {
    const pct = typeof v === 'number' ? Math.round(v) : 0;
    return `
      <div style="display:grid;grid-template-columns:46px 1fr 36px;align-items:center;
                  gap:6px;font-size:11px;color:var(--text-secondary);">
        <span>${escHtml(label)}</span>
        <span style="height:5px;border-radius:999px;background:var(--bg-primary);overflow:hidden;">
          <span style="display:block;width:${pct}%;height:100%;background:${color};"></span>
        </span>
        <span style="text-align:end;color:var(--text-primary);font-weight:600;">${pct}%</span>
      </div>`;
  };
  return `
    <div style="display:flex;flex-direction:column;gap:4px;">
      ${layer(t('weather_cloud_high'), high, '#94A3B8')}
      ${layer(t('weather_cloud_mid'), mid, '#64748B')}
      ${layer(t('weather_cloud_low'), low, '#475569')}
    </div>`;
}

function formatDayLength(sunriseIso: string, sunsetIso: string): string {
  const sr = new Date(sunriseIso).getTime();
  const ss = new Date(sunsetIso).getTime();
  if (!Number.isFinite(sr) || !Number.isFinite(ss) || ss <= sr) return '';
  const totalMin = Math.round((ss - sr) / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m.toString().padStart(2, '0')}`;
}

function europeanAqiBand(aqi: number, t: (k: string) => string): { label: string; color: string } {
  if (aqi >= 100) return { label: t('weather_aqi_very_poor'), color: '#9333EA' };
  if (aqi >= 80) return { label: t('weather_aqi_poor'), color: '#DC2626' };
  if (aqi >= 60) return { label: t('weather_aqi_moderate'), color: '#F59E0B' };
  if (aqi >= 40) return { label: t('weather_aqi_fair'), color: '#84CC16' };
  return { label: t('weather_aqi_good'), color: '#10B981' };
}

function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
